import React, { Component } from 'react'
import Calendar from 'react-big-calendar'
import moment from 'moment'
import EventComponent from 'containers/scheduler/event'
import { pathOr } from 'ramda'
import history from 'routes/history'

Calendar.tz = moment.tz.guess()
const mom = (...args) => moment.tz(...args, Calendar.tz)
mom.localeData = moment.localeData

Calendar.setLocalizer(Calendar.momentLocalizer(mom))

const formats = {
  dayFormat: 'ddd MM/DD',
}

class CalendarComponent extends Component {
  componentDidMount() {
    if (this.props.isModeratorManagement) {
      this.props.selectedModerator.id && this.props.selectedModerator.id >0 && this.props.getSlots({moderatorId: this.props.selectedModerator.id, clientId: this.props.selectedModerator.clientId})

    } else {
      this.props.user.userId  && this.props.getSlots()
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedModerator.id !== this.props.selectedModerator.id) {
      if (this.props.isModeratorManagement) { 
        this.props.selectedModerator.id && this.props.selectedModerator.id >0 && this.props.getSlots({moderatorId: this.props.selectedModerator.id, clientId: this.props.selectedModerator.clientId})
        this.props.selectedModerator.id >0 && this.props.fetchUser({moderatorId: this.props.selectedModerator.id})
      }
    } 
    if (prevProps.user.userId !== this.props.user.userId) {

      this.props.getSlots()
    }
  }
  
  selectSlot = ({ start, end }) => {
    /**
     * Adjust supplied dates to user specified timezone. This is important because
     * react-big-calendar provides js `Date`s for `start` and `end`which will be
     * localized to the system time.
     */
    const [startTime, endTime] = [start, end].map(date => this.parseTimeInUsersTimeZone(date, true))
    const isPMOrAdmin = this.props.user.userRole === 2 || this.props.user.userRole === 3
    if (isPMOrAdmin && !this.props.isModeratorManagement) {
    const  snackbarMessage =
      <span>
        {this.props.t('calendar.read.only.1')}
        &nbsp;
        <span className="copy-screeners-snackbar-link" onClick={() => history.push(`/moderatorManagement/schedule`)}>
        {this.props.t('calendar.read.only.2')}
        </span>
        &nbsp;
          <span>
          {this.props.t('calendar.read.only.3')}
          </span>
      </span>
      this.props.showMessage(snackbarMessage)
      this.props.hideMessage()
    } else {
      if (startTime.isBefore(moment())) {
        this.props.showMessage('You cannot define availability in the past.')
        this.props.hideMessage()
      } else {
        const selectedModerator =  this.props.selectedModerator 
        this.props.selectSlot({
          startTime,
          endTime,
          selectedModerator
        })
      }
    }

  }

  resizeEvent = (resizeType, { event, start, end }) => {
    if (event.eventType === 'availability') {
      this.props.putSlot({
        moderatorAvailabilityId: event.id,
        startTime: this.parseTimeInUsersTimeZone(start, true),
        endTime: this.parseTimeInUsersTimeZone(end, true),
      })
    }
  }

  styleEvents = ({ eventType, moderatorId, isImported, isInvalidatedInterview }) => {
    const isPMOrAdmin =  this.props.user.userRole === 2 || this.props.user.userRole === 3
    const colorCode = isImported? 'light-green' :  isPMOrAdmin && !this.props.isModeratorManagement? pathOr('lavender-purple', [moderatorId, 'className'], this.props.moderatorColorsById) : 'lavender-purple'
    return {
      className: eventType === 'availability'
        ? `moderator-availability ${colorCode}`
        : `time-slot ${colorCode}${isInvalidatedInterview ? ' invalidate-time-slot' : ''}`,
    }
  }

  selectEvent = event => this.props[
    event.eventType === 'timeSlot' ? 'toggleTimeSlotPopover' : 'toggleAvailabilityPopover'
  ](event)

  parseTimeInUsersTimeZone = (time, posting = false) => {
    const timeZone = this.props.isModeratorManagement? this.props.moderatorManagementTimeZone : this.props.userTimeZone
    const m = posting ? moment.tz(time, Calendar.tz) : moment.tz(time, timeZone)
    const dateUTCOffset = moment.tz(time, timeZone).utcOffset()
    m.utcOffset(dateUTCOffset, true) // keep local time

    return posting ? m : new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second())
  }

  parseStartTime = ({ virtualStartTime }) => this.parseTimeInUsersTimeZone(virtualStartTime)

  parseEndTime = ({ virtualEndTime }) => this.parseTimeInUsersTimeZone(virtualEndTime)

  /**
   * Handle quirks of react-big-calendar@0.17.0
   *
   * It should be noted that this is the last known stable version of RBC, as a
   * regression introduced by commit 9689b7d112dd9dba4a5424808de08fdd5738db79
   * causes events smaller than their containing step to be doubled in size.
   * ---------------------------------------------------------------------------
   * Considered Edge Cases (version agnostic at the time of writing):
   * 1) Events ending at the midnight boundary (00:00:00) disappear
   * 2) Events overlapping the midnight boundary are treated as "all day" events
   * ---------------------------------------------------------------------------
   * Solution:
   * Divide events overlapping the user timezone specific perception of midnight
   * into "virtual events" which point to the original event. If a virtual event
   * would end at midnight, reduce virtual end time by 1ms such that it can be
   * rendered (EC1). As such, no event will overlap midnight (EC2).
   */
  
  formatEventsForPoorlyWrittenLibrary = () =>
    this.props.availability
      .concat(this.props.timeSlots)
      .reduce((events, event) => {
        const { startTime, endTime } = event
        const msInDay = 86400000
        const msInHour = msInDay / 24
        // User's offset from UTC midnight in ms
        const msOffset = this.props.isModeratorManagement? (moment.tz(startTime, this.props.moderatorManagementTimeZone).utcOffset() * msInHour) / 60 : (moment.tz(startTime, this.props.userTimeZone).utcOffset() * msInHour) / 60
        const getNextMidnight = timestamp => Math.ceil((timestamp + msOffset) / msInDay) * msInDay - msOffset
        const isMidnight = timestamp => ((timestamp + msOffset) % msInDay) === 0
        const makeVirtualEvent = (start, end) => ({
          ...event,
          // no events should be rendered as "all day"
          allDay: false,
          // 0.17.0 - UI bug will show a "null" in hover state if this is undefined
          title: '',
          // Replicable but unique compound ID for virtual event
          virtualId: `${event.id}-${start}`,
          virtualStartTime: new Date(start),
          virtualEndTime: new Date(end),
          projectId: event.projectId,
          timeslotId: event.intervieweeId ? event.timeSlotId : -1,
        })

        const virtualEvents = []
        const end = endTime.getTime()
        let start = startTime.getTime()
        let nextMidnight = getNextMidnight(isMidnight(start) ? start + 1 : start)

        while (nextMidnight < end) {
          virtualEvents.push(makeVirtualEvent(start, nextMidnight - 1))

          start = nextMidnight
          nextMidnight = getNextMidnight(nextMidnight + 1)
        }

        virtualEvents.push(makeVirtualEvent(start, isMidnight(end) ? end - 1 : end))

        return events.concat(virtualEvents)
      }, [])

  render() {
    return (
      <Calendar
        events={this.formatEventsForPoorlyWrittenLibrary()}
        startAccessor={this.parseStartTime}
        endAccessor={this.parseEndTime}
        selectable={this.props.forAdmin ? false : 'ignoreEvents'}
        culture="en"
        timeslots={1}
        step={15}
        formats={formats}
        defaultView={Calendar.Views.WEEK}
        defaultDate={new Date()}
        scrollToTime={new Date(null, null, null, 8)}
        onSelectSlot={this.props.allowAddAv && this.selectSlot}
        onEventResize={this.resizeEvent}
        eventPropGetter={this.styleEvents}
        onSelectEvent={this.selectEvent}
        components={{ event: EventComponent }}
        views={['week', 'month', 'day', 'agenda']}
      />
    )
  }
}

export default CalendarComponent